import type { AuthErrorBody } from "@/services/api/types";

export interface AccountLockInfo {
  /** Absolute client-clock instant at which the account unlocks. */
  unlockAt: Date;
}

/**
 * Detect a failed-login lockout on a login error and resolve when the account
 * unlocks. A lockout is HTTP 403 with `code === "ACCOUNT_LOCKED"`.
 *
 * Prefers the server-computed `retryAfterSeconds` (relative, so it is immune to
 * client clock skew) and falls back to the absolute `lockedUntil` timestamp.
 * Returns `null` for any error that is not an account lockout.
 */
export function getAccountLock(error: unknown): AccountLockInfo | null {
  const err = error as {
    response?: { status?: number; data?: AuthErrorBody };
  };
  const data = err?.response?.data;

  if (err?.response?.status !== 403 || data?.code !== "ACCOUNT_LOCKED") {
    return null;
  }

  if (typeof data.retryAfterSeconds === "number" && data.retryAfterSeconds > 0) {
    return { unlockAt: new Date(Date.now() + data.retryAfterSeconds * 1000) };
  }

  if (data.lockedUntil) {
    const unlockAt = new Date(data.lockedUntil);
    if (!Number.isNaN(unlockAt.getTime())) {
      return { unlockAt };
    }
  }

  return null;
}

/** Format whole seconds as `m:ss` for a lockout countdown (e.g. `14:32`). */
export function formatLockCountdown(totalSeconds: number): string {
  const safe = Math.max(0, Math.floor(totalSeconds));
  const minutes = Math.floor(safe / 60);
  const seconds = safe % 60;
  return `${minutes}:${seconds.toString().padStart(2, "0")}`;
}
